import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/useAuth";
import { useBrowserNotification } from "../../hooks/useNotifications";
import { LIVE_CLASSES } from "../../utils/liveClassConfig";
import {
  getDay,
  setHours,
  setMinutes,
  setSeconds,
  setMilliseconds,
  differenceInMinutes,
  addMinutes,
  isBefore,
} from "date-fns";

const NOTIFIED_KEY = "kaizu_notified_";

function getClassDateToday(dayOfWeek: number, hour: number, minute: number): Date | null {
  const now = new Date();
  if (getDay(now) !== dayOfWeek) return null;

  const classDate = setMilliseconds(setSeconds(setMinutes(setHours(now, hour), minute), 0), 0);
  // Only return if class hasn't ended yet
  if (isBefore(addMinutes(classDate, 90), now)) return null;

  return classDate;
}

function getUserClasses(cohortId: number, educationType?: string) {
  return LIVE_CLASSES.filter((c) => {
    if (c.cohortIds && c.cohortIds.includes(cohortId)) return true;
    if (
      c.educationTypes &&
      educationType &&
      c.educationTypes.some((et) => educationType.toLowerCase().includes(et.toLowerCase()))
    )
      return true;
    return false;
  });
}

export default function NotificationManager() {
  const { user } = useAuth();
  const { requestPermission, sendNotification } = useBrowserNotification();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasRequestedRef = useRef(false);

  // Request permission on first interaction
  useEffect(() => {
    if (!user || hasRequestedRef.current) return;

    const handleInteraction = () => {
      requestPermission();
      hasRequestedRef.current = true;
      document.removeEventListener("click", handleInteraction);
    };

    document.addEventListener("click", handleInteraction);
    return () => document.removeEventListener("click", handleInteraction);
  }, [user, requestPermission]);

  // Check for upcoming classes every minute
  useEffect(() => {
    if (!user) return;

    const checkNotifications = () => {
      const now = new Date();
      const classes = getUserClasses(user.cohort_id, user.education_type);

      for (const cls of classes) {
        const classDate = getClassDateToday(cls.dayOfWeek, cls.hour, cls.minute);
        if (!classDate) continue;

        const minutesUntil = differenceInMinutes(classDate, now);
        const todayKey = `${NOTIFIED_KEY}${cls.id}_${now.toDateString()}`;

        // Notify 15 minutes before
        if (minutesUntil > 0 && minutesUntil <= 15) {
          const notifKey = `${todayKey}_15`;
          if (!localStorage.getItem(notifKey)) {
            localStorage.setItem(notifKey, "1");

            toast.info(
              `🎥 ${cls.label} ${minutesUntil} dakika sonra başlıyor!`,
              { autoClose: 10000 }
            );

            sendNotification(
              "Canlı Ders Hatırlatması",
              `${cls.label} ${minutesUntil} dakika sonra başlıyor!`
            );
          }
        }

        // Notify 5 minutes before
        if (minutesUntil > 0 && minutesUntil <= 5) {
          const notifKey = `${todayKey}_5`;
          if (!localStorage.getItem(notifKey)) {
            localStorage.setItem(notifKey, "1");

            toast.warning(
              `⏰ ${cls.label} birazdan başlıyor! Hazır mısın?`,
              { autoClose: 15000 }
            );

            sendNotification(
              "Ders Başlıyor!",
              `${cls.label} birazdan başlıyor, hazır ol!`
            );
          }
        }
      }

      // Clean up old notification flags (older than today)
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith(NOTIFIED_KEY) && !key.includes(now.toDateString())) {
          localStorage.removeItem(key);
        }
      }
    };

    // Initial check
    checkNotifications();

    // Check every minute
    intervalRef.current = setInterval(checkNotifications, 60_000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user, sendNotification]);

  return null; // Invisible component
}
